import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { useSocket } from '../../hooks/useSocket';
import * as notificationService from '../../services/notificationService';
import { colors } from '../../theme';

export default function NotificationBell() {
  const navigate = useNavigate();
  const { socket } = useSocket();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const load = () => {
    notificationService.listNotifications({ limit: 10 }).then((res) => {
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unreadCount);
    });
  };

  useEffect(load, []);

  useEffect(() => {
    if (!socket) return undefined;
    const handler = (notification) => {
      setNotifications((prev) => [notification, ...prev].slice(0, 10));
      setUnreadCount((c) => c + 1);
      // Desktop channel is inherently client-side - show the native
      // browser notification if the user has granted permission.
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        // eslint-disable-next-line no-new
        new Notification(notification.title, { body: notification.body });
      }
    };
    socket.on('notification:new', handler);
    return () => socket.off('notification:new', handler);
  }, [socket]);

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  };

  const handleMarkAllRead = async () => {
    await notificationService.markAllRead();
    setUnreadCount(0);
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
  };

  const handleClickNotification = async (n) => {
    if (!n.is_read) {
      await notificationService.markRead(n.id);
      setUnreadCount((c) => Math.max(0, c - 1));
      setNotifications((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: 1 } : x)));
    }
  };

  return (
    <>
      <Tooltip title="Notifications">
        <IconButton size="small" onClick={handleOpen}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsRoundedIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 360, maxHeight: 480 } }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.25 }}>
          <Typography variant="subtitle2">Notifications</Typography>
          <Stack direction="row" spacing={0.5}>
            {unreadCount > 0 && (
              <Button size="small" onClick={handleMarkAllRead}>
                Mark all read
              </Button>
            )}
            <IconButton size="small" onClick={() => { setAnchorEl(null); navigate('/notification-settings'); }}>
              <SettingsRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
        <Divider />
        {notifications.length === 0 && (
          <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        )}
        {notifications.map((n) => (
          <Box
            key={n.id}
            onClick={() => handleClickNotification(n)}
            sx={{
              px: 2,
              py: 1.25,
              cursor: 'pointer',
              borderBottom: `1px solid ${colors.slate[100]}`,
              bgcolor: n.is_read ? 'transparent' : 'action.hover',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: n.is_read ? 400 : 700 }}>
              {n.title}
            </Typography>
            {n.body && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>
                {n.body}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              {new Date(n.created_at).toLocaleString()}
            </Typography>
          </Box>
        ))}
      </Menu>
    </>
  );
}
